// M1.4 reminders inbox. Pending reminders listed by dueAt; per-row Done /
// Snooze 1h / Snooze tomorrow / Cancel buttons. + New reminder form below.
//
// Auto-refreshes on action. Future enhancement (M1.4b): onValue listener
// for realtime updates across tabs.

import { useEffect, useState } from 'react';
import { Brain } from '@/sdk/brain';
import { listReminders, updateReminderStatus, type BrainReminder } from '@/shared/brainMemory';

function fmtRelative(iso: string): string {
  const now = new Date();
  const t = new Date(iso);
  const diffMs = t.getTime() - now.getTime();
  const absSec = Math.abs(diffMs) / 1000;
  const dir = diffMs < 0 ? 'ago' : 'from now';
  if (absSec < 60) return `${Math.round(absSec)}s ${dir}`;
  if (absSec < 3600) return `${Math.round(absSec / 60)}m ${dir}`;
  if (absSec < 86400) return `${Math.round(absSec / 3600)}h ${dir}`;
  return `${Math.round(absSec / 86400)}d ${dir}`;
}

function isoIn(seconds: number): string {
  return new Date(Date.now() + seconds * 1000).toISOString();
}

function tomorrowMorningISO(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}

export function RemindersPanel() {
  const [reminders, setReminders] = useState<BrainReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [whenLocal, setWhenLocal] = useState(() => {
    const d = new Date(Date.now() + 24 * 3600 * 1000);
    // Format for <input type="datetime-local">
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const rs = await listReminders('pending');
      setReminders(rs);
    } catch (e) {
      setError(`Failed to load reminders: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function createNew() {
    if (!text.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const dueAt = new Date(whenLocal).toISOString();
      await Brain.remind({ text, dueAt });
      setText('');
      await refresh();
    } catch (e) {
      setError(`Failed to create reminder: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function actOn(id: string, action: 'done' | 'cancel' | 'snooze-1h' | 'snooze-tomorrow') {
    setBusy(true);
    setError(null);
    try {
      if (action === 'done') await updateReminderStatus(id, 'done');
      else if (action === 'cancel') await updateReminderStatus(id, 'cancelled');
      else if (action === 'snooze-1h') {
        await updateReminderStatus(id, 'snoozed', { snoozedUntil: isoIn(3600) });
      } else if (action === 'snooze-tomorrow') {
        await updateReminderStatus(id, 'snoozed', { snoozedUntil: tomorrowMorningISO() });
      }
      await refresh();
    } catch (e) {
      setError(`Action failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-paper border border-line rounded p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-semibold text-navy">Reminders (M1.4)</h2>
        <button
          onClick={refresh}
          disabled={loading}
          className="text-xs text-text-mute hover:text-orange"
        >
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
      </div>
      <p className="text-xs text-text-mute mb-4">
        Persistent reminders backed by Firebase. Action buttons mark done, snooze, or cancel.
      </p>

      {/* New reminder form */}
      <div className="border border-orange/30 bg-orange/5 rounded p-3 mb-4">
        <div className="text-sm font-semibold mb-2">+ New reminder</div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Call Acme about Q-1042"
            className="px-2 py-1 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
          <input
            type="datetime-local"
            value={whenLocal}
            onChange={(e) => setWhenLocal(e.target.value)}
            className="px-2 py-1 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
          <button
            onClick={createNew}
            disabled={busy || !text.trim()}
            className="px-3 py-1 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>

      {/* List */}
      {reminders.length === 0 ? (
        <div className="text-sm text-text-mute italic text-center py-4">No pending reminders.</div>
      ) : (
        <ul className="space-y-2">
          {reminders.map((r) => {
            const overdue = new Date(r.dueAt) < new Date();
            return (
              <li
                key={r.id}
                className={`border rounded p-2 text-sm ${overdue ? 'border-orange bg-orange/5' : 'border-line'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{r.text}</div>
                    <div className={`text-xs mt-1 ${overdue ? 'text-orange font-semibold' : 'text-text-mute'}`}>
                      {overdue ? '⚠ ' : ''}
                      Due {fmtRelative(r.dueAt)} ·{' '}
                      <span className="font-mono">{new Date(r.dueAt).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 text-xs">
                    <button
                      onClick={() => actOn(r.id, 'done')}
                      disabled={busy}
                      className="px-2 py-1 bg-green/20 hover:bg-green/30 text-green rounded"
                    >
                      ✓ Done
                    </button>
                    <button
                      onClick={() => actOn(r.id, 'snooze-1h')}
                      disabled={busy}
                      className="px-2 py-1 bg-bg hover:bg-line text-text-mute rounded"
                    >
                      Snooze 1h
                    </button>
                    <button
                      onClick={() => actOn(r.id, 'snooze-tomorrow')}
                      disabled={busy}
                      className="px-2 py-1 bg-bg hover:bg-line text-text-mute rounded"
                    >
                      Tomorrow 9am
                    </button>
                    <button
                      onClick={() => actOn(r.id, 'cancel')}
                      disabled={busy}
                      className="px-2 py-1 bg-bg hover:bg-orange/20 hover:text-orange text-text-mute rounded"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <div className="mt-3 bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm">
          ⚠ {error}
        </div>
      )}
    </div>
  );
}
